/* eslint-disable jsx-a11y/anchor-has-content */
import React, { useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

// project-imports
import { slugify } from "../../utils";
import { GoogleAnalyticsEnum, pageView, triggerFunction, } from "../../utils/google-analytics";
import SEO from "../../common/SEO";
import Layout from "../../common/Layout";
import Spinner from "../../components/spinner-component/SpinnerOne";
import BreadcrumbOne from "../../common/breadcrumb/BreadcrumbOne";
import { useProductContext } from "../../context/product_context";
import TrainingProgramInfo from "../../components/training-programs/TrainingProgramInfo";
import SectionTitle from "../../components/sectionTitle/SectionTitle";
import ReusableWidget from "../../components/reusable/widgets/ReusableWidget";
import { useGalleryContext } from "../../context/gallery_context";
import FsLightbox from "fslightbox-react";
import GalleryDrawer from "../../components/our-gallery/GalleryDrawer";
import InterviewRelatedSeries from "../../components/interview-questions/interview-series/InterviewRelatedSeries";
import { useInterviewContext } from "../../context/interview_context";
import EBookWidget from "../../components/reusable/widgets/EBookWidget";

// Reading variables from .env
const TYPE_DEVELOPER = process.env.REACT_APP_OFFERING_TYPE_DEVELOPER;
const TYPE_ADMIN = process.env.REACT_APP_OFFERING_TYPE_ADMIN;

const TrainingProgramDetails = () => {

    // Extracting methods and variables form the context
    const { products, productLoading, productsError } = useProductContext();
    const { popularVideos, popularShorts, popularLiveVideos, lastWeekTrendingVideos, latestVideos, isLoading } = useGalleryContext();
    const { interviewSeries } = useInterviewContext();

    // Property initialization
    const { type, productCode } = useParams();
    const location = useLocation();

    // Consant variable
    const trainingProgramProductCode = productCode || "";
    const trainingProgramType = type || "";
    const data = (products || [])?.filter((item) => {
        const currentBookType = item?.category === "salesforce" && item?.subCategory === "administration" ? TYPE_ADMIN : TYPE_DEVELOPER;
        return currentBookType === trainingProgramType && item?.productCode?.toLowerCase() === trainingProgramProductCode;
    }) || [];

    const trainingProgramItem = data && data[0] && (data[0] || {});

    const metaDataObject = trainingProgramItem && trainingProgramItem?.metadata ? trainingProgramItem?.metadata : {};
    const metaDataTags =
        metaDataObject && metaDataObject?.tags ? metaDataObject?.tags : "";
    const resourceFeatures =
        trainingProgramItem && trainingProgramItem?.features && trainingProgramItem?.features?.length ? trainingProgramItem?.features : [];
    const tags = metaDataTags.split(/[,\s]+/);
    const billing = trainingProgramItem && trainingProgramItem?.pricing && trainingProgramItem?.pricing?.billingCycle ? trainingProgramItem?.pricing?.billingCycle : "";

    // Constant variable
    const popularVideosData = popularVideos && popularVideos?.length && popularVideos ? popularVideos : [];
    const popularShortsVideosData = popularShorts && popularShorts?.length && popularShorts ? popularShorts : [];
    const popularLiveVideosData = popularLiveVideos && popularLiveVideos?.length && popularLiveVideos ? popularLiveVideos : [];
    const lastWeekTrendingVideosData = ((lastWeekTrendingVideos && Object.keys(lastWeekTrendingVideos)?.length) ? lastWeekTrendingVideos?.lastWeek : lastWeekTrendingVideos?.twoWeeksAgo) || [];
    const latestVideosData = latestVideos && latestVideos?.length && latestVideos ? latestVideos : [];

    const seriesData = interviewSeries && interviewSeries?.length ? interviewSeries : [];
    const seriesName = seriesData?.map((i) => i?.name || "");

    // State initalization
    const [contentTab, setContentTab] = useState("overview");
    const [toggler, setToggler] = useState(false);
    const [videoUrl, setVideoUrl] = useState("");
    const [openDrawer, setOpenDrawer] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const ebookTitleForAnalytics = trainingProgramItem
        ? GoogleAnalyticsEnum.SF_EBOOK_DETAIL_VIEW + " " + trainingProgramItem?.name
        : "Default Training Program";

    // Constant functions
    const handleTab = (content) => {
        if (content === "overview") {
            setContentTab("overview");
        }
    };

    const handleVideoToggle = (url) => {
        try {
            setVideoUrl(url);
            setToggler(!toggler);
        } catch (error) { }
    };

    const handleOpenDrawer = (data) => {
        try {
            setOpenDrawer(true);
            setSelectedItem(data);
            triggerFunction(
                GoogleAnalyticsEnum.BUTTON,
                GoogleAnalyticsEnum.SF_TUTORIALS_DETAIL_VIEW,
            );
        } catch (error) {
            console.log(
                "49 : HomeFourBlog : Error in handleOpenDrawer : ",
                error && JSON.stringify(error),
            );
        }
    };

    const handleHideDrawer = () => {
        try {
            setOpenDrawer(false);
        } catch (error) {
            console.log(
                "60 : HomeFourBlog : Error in handleHideDrawer : ",
                error && JSON.stringify(error),
            );
        }
    };

    useEffect(() => {
        //  Get hash from URL
        const hash = location.hash;

        if (hash) {
            const tabName = hash.replace('#', '');
            if (tabName) {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth' // Use 'auto' for an instant jump instead of smooth scrolling
                });
                setContentTab(tabName);
            }
        }
    }, []);



    return (
        <>
            <SEO title={trainingProgramItem?.name || "Default Training Program Title"} url={process.env.PUBLIC_URL + `/our-offerings/${trainingProgramType}/${slugify(trainingProgramItem?.name || "Default Taining Program Title")}`} image={trainingProgramItem?.image} />  {/* Set SEO title */}
            {pageView(window.location.pathname, ebookTitleForAnalytics)}
            {productLoading && <Spinner />}  {/* Loading spinner */}
            <Layout searchDisable={true}>
                <BreadcrumbOne
                    title={`${trainingProgramItem?.name}(${billing})`} // Breadcrumb title 
                    rootUrl="/"
                    parentUrl="Home"
                    googleEnum={GoogleAnalyticsEnum.SALESFORCE_EBOOK_DETAILS_TO_HOME}
                    currentUrl="Pricing Details"
                />
                <div className="edu-course-details-area pt--40 pb--140 bg-color-white">
                    <div className="container eduvibe-animated-shape">

                        <SectionTitle
                            classes="text-start"
                            slogan="Our Offerings"
                            title={trainingProgramItem?.name}
                        />

                        <div className="row g-5 pt--40">
                            <div className="col-12 col-xl-8 col-lg-7">
                                <div className="course-details-content">

                                    <ul className="edu-course-tab nav nav-tabs justify-content-start" role="tablist">
                                        <li className="nav-item">
                                            <button
                                                className={
                                                    contentTab === "overview"
                                                        ? "nav-link active"
                                                        : "nav-link"
                                                }
                                                type="button"
                                                aria-label="Overview"
                                                data-target="overview"
                                                onClick={() => {
                                                    handleTab("overview");
                                                    triggerFunction(
                                                        GoogleAnalyticsEnum.BUTTON,
                                                        GoogleAnalyticsEnum.SALESFORCE_EBOOK_DETAILS_OVERVIEW_BUTTON
                                                    );
                                                }}
                                            >
                                                Overview
                                            </button>
                                        </li>
                                    </ul>

                                    <div className="tab-content">
                                        {contentTab === "overview" && (
                                            <div
                                                className={`tab-pane fade show ${contentTab === "overview" ? "active" : ""
                                                    } `}
                                            >
                                                <div className="col-12 pt-5">
                                                    <div className="row px-4">
                                                        {/* <div className="col-12 text-start">
                                                            <div className="fw-bold fs-2">Description</div>
                                                        </div> */}
                                                        <div className="col-12 text-start">
                                                            <p className="m-0" dangerouslySetInnerHTML={{ __html: trainingProgramItem?.description }}></p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {resourceFeatures && (
                                                    <div className="col-12">
                                                        <div className="row px-4">
                                                            <div className="col-12 text-start">
                                                                <div className="fw-bold text-dark fs-2">Features</div>
                                                            </div>
                                                            <div className="col-12 text-start mb-2">
                                                                <ul className="mb-0">
                                                                    {(resourceFeatures || [])?.map((item, i) => (
                                                                        <li key={i}>{item}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {tags && tags?.length && (
                                                    <div className="col-12">
                                                        <div className="row px-4">
                                                            <div className="col-12 text-start">
                                                                <div className="fw-bold text-dark fs-2">Tags</div>
                                                            </div>
                                                            <div className="rating eduvibe-course-rating-stars mt-0 text-start">
                                                                {(tags || [])?.map((item, i) => (
                                                                    <span
                                                                        class="fs-5 badge text-success rounded-pill bg-success bg-opacity-25 text-capitalize mx-1"
                                                                        key={i}
                                                                    >
                                                                        {item}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-xl-4 col-lg-5">
                                <TrainingProgramInfo data={trainingProgramItem} />
                                <ReusableWidget data={lastWeekTrendingVideosData} title="Last Week Trending Videos" href="https://www.youtube.com/@LongswitchAcademy/videos" onVideoToggle={handleVideoToggle} onOpenDrawer={handleOpenDrawer} classes="mt--20" />
                                <ReusableWidget data={latestVideosData} title="Latest Videos" href="https://www.youtube.com/@LongswitchAcademy/videos" onVideoToggle={handleVideoToggle} onOpenDrawer={handleOpenDrawer} classes="mt--10" />
                                <ReusableWidget data={popularVideosData} title="Popular Videos" href="https://www.youtube.com/@LongswitchAcademy/videos" onVideoToggle={handleVideoToggle} onOpenDrawer={handleOpenDrawer} classes="mt--10" />
                                <ReusableWidget data={popularShortsVideosData} title="Popular Shorts Videos" href="https://www.youtube.com/@LongswitchAcademy/shorts" onVideoToggle={handleVideoToggle} onOpenDrawer={handleOpenDrawer} classes="mt--10" />
                                <ReusableWidget data={popularLiveVideosData} title="Popular Live Videos" href="https://www.youtube.com/@LongswitchAcademy/streams" onVideoToggle={handleVideoToggle} onOpenDrawer={handleOpenDrawer} classes="mt--10" />

                                <InterviewRelatedSeries isFromWidget={true} heading="Series You May Like" currentSeries={seriesName} classes="mt--20" />
                                <EBookWidget classes="mt--20" />
                            </div>
                        </div>

                    </div>
                </div>
                <div className="video-lightbox-wrapper">
                    <FsLightbox
                        toggler={toggler}
                        sources={[videoUrl]}
                        maxYoutubeVideoDimensions={
                            { width: 1920, height: 1080 }
                        }
                    />
                </div>

                {selectedItem && (
                    <GalleryDrawer
                        show={openDrawer}
                        classes={null}
                        onClose={handleHideDrawer}
                        data={selectedItem}
                        onVideoToggle={handleVideoToggle}
                        title={"Salesforce Tutorials Info"}
                        isLoading={isLoading}
                    />
                )}
            </Layout>
        </>
    );
};
export default TrainingProgramDetails;
